import React, { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import moment from "moment";
import ShopifyLogo from "../Pictures/ShopifyLogo";

interface Connection {
  shop_domain: string;
  created_at: string;
  updated_at: string;
}

interface ConnectionsListProps {
  connections: Connection[];
  onDelete?: (shopDomain: string) => void; // Optional callback for delete
}

const ConnectionsList: React.FC<ConnectionsListProps> = ({
  connections,
  onDelete,
}) => {
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedShop, setSelectedShop] = useState<string | null>(null);

  const handleOpenConfirm = (shopDomain: string) => {
    setSelectedShop(shopDomain);
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setSelectedShop(null);
    setOpenConfirm(false);
  };

  const handleConfirmDelete = () => {
    if (selectedShop && onDelete) {
      onDelete(selectedShop);
    }
    handleCloseConfirm();
  };

  return (
    <>
      <Stack spacing={2}>
        {connections.map((connection) => (
          <Paper
            key={connection.shop_domain}
            elevation={3}
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: 6,
              },
            }}
          >
            {/* Left Side: Logo + Info */}
            <Stack direction="row" spacing={2} alignItems="center">
              <ShopifyLogo size={48} />

              <Box>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                  {connection.shop_domain}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Created:{" "}
                  {moment
                    .unix(parseInt(connection.created_at, 10) / 1000)
                    .format("MMM D, YYYY h:mm A")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Updated:{" "}
                  {moment
                    .unix(parseInt(connection.updated_at, 10) / 1000)
                    .format("MMM D, YYYY h:mm A")}
                </Typography>
              </Box>
            </Stack>

            {/* Right Side: Delete Button */}
            {onDelete && (
              <IconButton
                color="error"
                aria-label="delete"
                onClick={() => handleOpenConfirm(connection.shop_domain)}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Paper>
        ))}
      </Stack>

      {/* Confirm Delete Dialog */}
      <Dialog open={openConfirm} onClose={handleCloseConfirm}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the connection{" "}
            <strong>{selectedShop}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConnectionsList;
